import { useCallback, useEffect, useState } from "preact/hooks";

/**
 * Where the console is, kept in the URL hash so the back button works and a
 * reload opens the same conversation. No router library: two screens and an id.
 *
 * Two hashes carry a query as well as a path: the set-password link mailed by
 * an approval (`#/set-password?token=…`) and Stripe's return
 * (`#/settings/billing?checkout=success`). Everything after the `?` is parsed
 * as a query and never as part of the path.
 */

export type SettingsTab =
  | "profile"
  | "notifications"
  | "security"
  | "sites"
  | "widget"
  | "canned"
  | "triggers"
  | "hours"
  | "spam"
  | "operators"
  | "audit"
  | "billing";

const SETTINGS_TABS: readonly SettingsTab[] = [
  "profile",
  "notifications",
  "security",
  "sites",
  "widget",
  "canned",
  "triggers",
  "hours",
  "spam",
  "operators",
  "audit",
  "billing",
];

export type Route =
  | { view: "inbox"; id: number | null }
  | { view: "history"; id: number | null }
  | { view: "stats" }
  /** Who is on the sites right now; every operator, for the sites they serve. */
  | { view: "visitors" }
  | { view: "settings"; tab: SettingsTab }
  /** Platform admins only — see NavRail's navKeys and App's route gate. */
  | { view: "earlyAccess" }
  /** Platform admins only: every workspace, its plan and its owner. */
  | { view: "customers" }
  /** Public: reached from an invite e-mail while signed out. */
  | { view: "setPassword"; token: string };

/** The `?…` part of a hash, if any. Exported because Stripe's return is read from it. */
export function hashQuery(hash: string): URLSearchParams {
  const at = hash.indexOf("?");
  return new URLSearchParams(at === -1 ? "" : hash.slice(at + 1));
}

/** How Stripe came back, for the notice on the billing tab. */
export function checkoutResult(hash: string): "success" | "cancel" | null {
  const value = hashQuery(hash).get("checkout");
  return value === "success" || value === "cancel" ? value : null;
}

export function parseRoute(hash: string): Route {
  const at = hash.indexOf("?");
  const path = (at === -1 ? hash : hash.slice(0, at)).replace(/^#\/?/, "");
  const parts = path.split("/");
  if (parts[0] === "set-password") return { view: "setPassword", token: hashQuery(hash).get("token") ?? "" };
  if (parts[0] === "settings") {
    const tab = SETTINGS_TABS.find((t) => t === parts[1]) ?? "profile";
    return { view: "settings", tab };
  }
  if (parts[0] === "stats") return { view: "stats" };
  if (parts[0] === "visitors") return { view: "visitors" };
  if (parts[0] === "early-access") return { view: "earlyAccess" };
  if (parts[0] === "customers") return { view: "customers" };
  const id = Number(parts[1]);
  if (parts[0] === "history") return { view: "history", id: Number.isInteger(id) && id > 0 ? id : null };
  return { view: "inbox", id: parts[0] === "inbox" && Number.isInteger(id) && id > 0 ? id : null };
}

export function routeHash(route: Route): string {
  if (route.view === "settings") return `#/settings/${route.tab}`;
  if (route.view === "stats") return "#/stats";
  if (route.view === "visitors") return "#/visitors";
  if (route.view === "earlyAccess") return "#/early-access";
  if (route.view === "customers") return "#/customers";
  if (route.view === "setPassword") {
    return route.token ? `#/set-password?token=${encodeURIComponent(route.token)}` : "#/set-password";
  }
  if (route.view === "history") return route.id ? `#/history/${route.id}` : "#/history";
  return route.id ? `#/inbox/${route.id}` : "#/inbox";
}

/**
 * Where a route actually renders, once who is looking is known. Direct-URL
 * access to a platform-admin screen by anyone else falls back to the inbox
 * rather than showing (or erroring on) a screen that doesn't exist for them —
 * the same treatment SettingsView gives an admin-only tab.
 */
export function effectiveRoute(route: Route, isPlatformAdmin: boolean): Route {
  if ((route.view === "earlyAccess" || route.view === "customers") && !isPlatformAdmin) {
    return { view: "inbox", id: null };
  }
  return route;
}

/**
 * Whether the suspended notice replaces this screen.
 *
 * A suspended workspace can do nothing — except pay. The owner keeps the
 * billing tab, and a platform admin keeps the screens the server exempts
 * them from (their own workspace being suspended must not lock them out of
 * un-suspending it).
 */
export function blockedBySuspension(
  route: Route,
  who: { isOwner: boolean; isPlatformAdmin: boolean },
): boolean {
  if (route.view === "settings" && route.tab === "billing" && who.isOwner) return false;
  if ((route.view === "earlyAccess" || route.view === "customers") && who.isPlatformAdmin) return false;
  return true;
}

export function useRoute(): [Route, (next: Route) => void] {
  const [route, setRoute] = useState<Route>(() => parseRoute(location.hash));
  useEffect(() => {
    const onChange = (): void => setRoute(parseRoute(location.hash));
    window.addEventListener("hashchange", onChange);
    return () => window.removeEventListener("hashchange", onChange);
  }, []);
  // Stable, because views put navigation inside effect dependencies.
  const go = useCallback((next: Route): void => {
    const hash = routeHash(next);
    if (location.hash !== hash) location.hash = hash;
    else setRoute(next);
  }, []);
  return [route, go];
}
